import { getHymnsBySection, Hymn } from "@/services/hymnsApi";
import { getHymnSlug, scrollToTop } from "@/utils";
import {
  AlertTriangle,
  ChevronRight,
  ListMusic,
  RefreshCw,
  Search,
  Volume2,
  X,
} from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";

// Module-level cache to prevent re-fetching the section hymns catalog when jumping between hymns
const sectionHymnsCache = new Map<string, Hymn[]>();

interface BadgeInfo {
  badge?: string;
  name?: string;
  languageName?: string;
}

interface SongDetailFlatListPanelProps {
  sectionCode?: string;
  currentNumber: number;
  badgeInfo: BadgeInfo;
  isPrimaryLoading?: boolean;
  className?: string;
  onSelectSong?: (hymn: Hymn) => void;
}

export const SongDetailFlatListPanel: React.FC<
  SongDetailFlatListPanelProps
> = ({
  sectionCode,
  currentNumber,
  badgeInfo,
  isPrimaryLoading = false,
  className = "",
  onSelectSong,
}) => {
  const navigate = useNavigate();
  const [sectionHymns, setSectionHymns] = useState<Hymn[]>(() => {
    if (sectionCode && sectionHymnsCache.has(sectionCode)) {
      return sectionHymnsCache.get(sectionCode)!;
    }
    return [];
  });

  const [loadingSectionHymns, setLoadingSectionHymns] = useState<boolean>(
    () => {
      if (sectionCode && sectionHymnsCache.has(sectionCode)) {
        return false;
      }
      return true;
    },
  );

  const [errorSectionHymns, setErrorSectionHymns] = useState<boolean>(false);
  const [sidebarSearch, setSidebarSearch] = useState<string>("");
  const activeItemRef = useRef<HTMLDivElement | null>(null);

  const loadSectionHymns = useCallback(async () => {
    if (!sectionCode) return;
    if (sectionHymnsCache.has(sectionCode)) {
      setSectionHymns(sectionHymnsCache.get(sectionCode)!);
      setLoadingSectionHymns(false);
      return;
    }
    setLoadingSectionHymns(true);
    setErrorSectionHymns(false);
    try {
      const data = await getHymnsBySection(sectionCode, undefined, 600);
      sectionHymnsCache.set(sectionCode, data);
      setSectionHymns(data);
    } catch (err) {
      console.error("Error loading section hymns for sidebar:", err);
      setErrorSectionHymns(true);
    } finally {
      setLoadingSectionHymns(false);
    }
  }, [sectionCode]);

  // Load section hymns asynchronously once primary content is done loading
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!isPrimaryLoading) {
      timer = setTimeout(() => {
        loadSectionHymns();
      }, 50);
    }
    return () => clearTimeout(timer);
  }, [isPrimaryLoading, loadSectionHymns]);

  // Auto-scroll the active hymn in the sidebar into view
  useEffect(() => {
    if (!loadingSectionHymns && activeItemRef.current) {
      activeItemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [currentNumber, loadingSectionHymns, sectionHymns]);

  const filteredSectionHymns = useMemo(() => {
    if (!sidebarSearch.trim()) return sectionHymns;
    const q = sidebarSearch.toLowerCase().trim();
    return sectionHymns.filter((h) => {
      const matchNum = String(h.number).includes(q);
      const matchTitle = (h.title || "").toLowerCase().includes(q);
      return matchNum || matchTitle;
    });
  }, [sectionHymns, sidebarSearch]);

  const handleSelect = (hymn: Hymn) => {
    if (onSelectSong) {
      onSelectSong(hymn);
      return;
    }
    scrollToTop();
    const slug = getHymnSlug(hymn.number, hymn.title);
    navigate(
      `/chants-desperance/${encodeURIComponent(sectionCode || "")}/${slug}`,
    );
  };

  const cleanName = (badgeInfo.name || "")
    .replace(/\s*\([^)]*\)\s*$/, "")
    .trim();
  const effectiveLanguage =
    badgeInfo.languageName ||
    (sectionCode?.endsWith("-FR")
      ? "Français"
      : sectionCode?.endsWith("-CR")
        ? "Kreyòl"
        : "");

  const titleWithLang =
    effectiveLanguage &&
    !cleanName.toLowerCase().includes(effectiveLanguage.toLowerCase())
      ? `${cleanName} (${effectiveLanguage})`
      : badgeInfo.name || cleanName;

  return (
    <aside
      className={`flex flex-col rounded-2xl bg-slate-900/80 border border-slate-800/80 shadow-lg backdrop-blur-md overflow-hidden ${className}`}
    >
      {/* Sidebar Header */}
      <div className="p-3 border-b border-slate-800/60 bg-slate-900/90 flex flex-col gap-2 flex-shrink-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-md bg-slate-800 border border-slate-700/60 flex items-center justify-center flex-shrink-0 text-sky-400 font-serif font-bold text-[11px]">
              {badgeInfo.badge}
            </div>
            <div className="min-w-0">
              <h2 className="font-semibold text-slate-200 text-xs truncate">
                {titleWithLang}
              </h2>
              <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                {effectiveLanguage && (
                  <span className="text-amber-400/90 font-medium">
                    {effectiveLanguage}
                  </span>
                )}
                {effectiveLanguage && <span>•</span>}
                <span>{sectionHymns.length} cantiques</span>
              </div>
            </div>
          </div>

          <Link
            to={`/chants-desperance/${encodeURIComponent(sectionCode || "")}`}
            className="text-slate-500 hover:text-slate-300 p-1 rounded-md hover:bg-slate-800/60 transition-colors flex-shrink-0"
            title={`Voir tout ${badgeInfo.name}`}
          >
            <ListMusic className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Quick Search inside Section */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <input
            type="text"
            value={sidebarSearch}
            onChange={(e) => setSidebarSearch(e.target.value)}
            placeholder="Filtrer par N° ou titre..."
            className="w-full pl-8 pr-7 py-1 rounded-md bg-slate-950/60 border border-slate-800/80 text-slate-200 placeholder-slate-500 text-xs focus:outline-none focus:border-slate-700"
          />
          {sidebarSearch && (
            <button
              onClick={() => setSidebarSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* Loading State in Sidebar */}
      {loadingSectionHymns && (
        <div className="flex-1 overflow-hidden p-1.5 space-y-1 animate-pulse">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="px-2 py-1.5 rounded-lg bg-slate-950/20 border border-slate-800/30 flex items-center justify-between gap-2"
            >
              <div className="flex items-center gap-2 min-w-0 w-full">
                <div className="w-6 h-6 rounded-md bg-slate-800/60 flex-shrink-0" />
                <div className="space-y-1 flex-1 min-w-0">
                  <div
                    className="h-2.5 bg-slate-800/60 rounded"
                    style={{ width: `${55 + (i % 4) * 10}%` }}
                  />
                  <div className="h-2 bg-slate-800/30 rounded w-1/4" />
                </div>
              </div>
              <div className="w-3 h-3 rounded-full bg-slate-800/40 flex-shrink-0" />
            </div>
          ))}
        </div>
      )}

      {/* Error State in Sidebar */}
      {!loadingSectionHymns && errorSectionHymns && (
        <div className="p-3 text-center">
          <AlertTriangle className="w-4 h-4 text-rose-400 mx-auto mb-1" />
          <p className="text-slate-400 text-xs mb-1.5">
            Erreur de chargement des cantiques.
          </p>
          <button
            onClick={loadSectionHymns}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium text-xs border border-slate-700 transition-all"
          >
            <RefreshCw className="w-3 h-3" />
            <span>Réessayer</span>
          </button>
        </div>
      )}

      {/* Song List in Sidebar */}
      {!loadingSectionHymns && !errorSectionHymns && (
        <div className="flex-1 overflow-y-auto p-1 space-y-0.5 scrollbar-tiny">
          {filteredSectionHymns.map((h) => {
            const isActive = h.number === currentNumber;
            const hasAudio = Boolean(h.media_url);

            return (
              <div
                key={h.id || `${h.section_code}-${h.number}`}
                ref={isActive ? activeItemRef : undefined}
                onClick={() => handleSelect(h)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleSelect(h);
                  }
                }}
                className={`group relative flex items-center justify-between px-2 py-1.5 rounded-lg border transition-colors cursor-pointer text-left ${
                  isActive
                    ? "bg-slate-800/70 border-slate-700/80 text-slate-100"
                    : "bg-slate-950/20 hover:bg-slate-800/40 border-transparent hover:border-slate-800/50 text-slate-400 hover:text-slate-200"
                }`}
              >
                <div className="flex items-center gap-2 min-w-0 pr-1">
                  {/* Number Badge */}
                  <div
                    className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 text-[11px] transition-colors ${
                      isActive
                        ? "bg-sky-500/20 text-amber-300 border border-amber-500/40 font-semibold"
                        : "bg-slate-900/70 text-slate-400 border border-slate-800/60 font-medium group-hover:text-slate-300 group-hover:border-slate-700"
                    }`}
                  >
                    {h.number}
                  </div>

                  {/* Title */}
                  <div className="min-w-0">
                    <span
                      className={`block text-xs truncate leading-tight ${
                        isActive
                          ? "text-slate-100 font-medium"
                          : "text-slate-400 group-hover:text-slate-200"
                      }`}
                    >
                      {h.title}
                    </span>
                    {h.category && (
                      <span className="text-[10px] text-slate-500 truncate block leading-none mt-0.5">
                        {h.category}
                      </span>
                    )}
                  </div>
                </div>

                {/* Audio Indicator */}
                <div className="flex items-center gap-1 flex-shrink-0 text-slate-500">
                  {hasAudio && (
                    <Volume2
                      className={`w-3 h-3 ${isActive ? "text-emerald-400" : "text-emerald-500/60"}`}
                    />
                  )}
                  <ChevronRight
                    className={`w-3 h-3 transition-transform ${
                      isActive
                        ? "text-slate-300"
                        : "text-slate-600 group-hover:text-slate-400 group-hover:translate-x-0.5"
                    }`}
                  />
                </div>
              </div>
            );
          })}

          {filteredSectionHymns.length === 0 && (
            <div className="p-3 text-center text-slate-500 text-xs">
              Aucun chant ne correspond à "{sidebarSearch}".
            </div>
          )}
        </div>
      )}
    </aside>
  );
};
